import type { Perk } from './schema';

/**
 * Perks (certificates, upgrade awards, vouchers, credits) as things that get used up: a
 * quantity, a status, the uses, and a history of every change kept on the perk itself.
 */

export type PerkStatus = 'active' | 'used' | 'expired';
export type PerkAction = 'add' | 'edit' | 'use' | 'restore' | 'delete' | 'expire';

export interface PerkChange {
  field: string;
  from: unknown;
  to: unknown;
}

/** Where a perk stands today. Older records only had `used`. */
export function perkStatus(p: Perk, today: string): PerkStatus {
  const status = p.status ?? (p.used ? 'used' : 'active');
  if (status === 'active' && p.expiresOn && p.expiresOn < today) return 'expired';
  return status;
}

export const perkQuantity = (p: Perk) => p.quantity ?? 1;

/** Still there to use: not deleted, not used up, not expired. */
export const isActivePerk = (p: Perk, today: string) =>
  !p.deleted && perkStatus(p, today) === 'active';

/** "Hyatt Guest of Honor awards (8)" → the title without the count, and the count. */
export function splitGroupedTitle(title: string): { title: string; quantity: number } | null {
  const m = /^(.*\S)\s*\((\d{1,3})\)\s*$/.exec(title);
  if (!m?.[1] || !m[2]) return null;
  const n = Number(m[2]);
  return n >= 1 ? { title: m[1], quantity: n } : null;
}

/** The fields that changed between two versions of a perk, for its history. */
export function perkDiff(a: Partial<Perk>, b: Partial<Perk>): PerkChange[] {
  const keys = [
    'kind',
    'title',
    'programId',
    'cardId',
    'value',
    'unit',
    'expiresOn',
    'quantity',
    'status',
    'usedOn',
    'usedFor',
    'planId',
    'redemptionId',
    'notes',
    'progress',
  ] as const;
  const out: PerkChange[] = [];
  for (const k of keys) {
    const from = a[k] ?? null;
    const to = b[k] ?? null;
    if (JSON.stringify(from) !== JSON.stringify(to)) out.push({ field: k, from, to });
  }
  return out;
}

/** A history entry, kept on the perk (newest last, at most 200). */
export function withHistory(
  p: Perk,
  action: PerkAction,
  changes: PerkChange[],
  at: number,
  note?: string,
): NonNullable<Perk['history']> {
  const entry = { at, action, changes, ...(note ? { note } : {}) };
  return [...(p.history ?? []), entry].slice(-200);
}
